import { useState } from 'react'

import { FvButton } from '@fv/client-components'
import type { FullShipment, SpotQuoteContact } from '@fv/client-types'
import { ALL_CONTACTS_GROUP_ID } from '@fv/models'

import InfoBox from '@/components/InfoBox'
import { ValidatedForm } from '@/components/inputs'
import { DetailPanelLayout } from '@/components/list/DetailPanelLayout'
import { supportMessage } from '@/constants'
import { useSpotQuoteRecipients } from '@/hooks/spotQuotes'

import { useSpotQuoteGroups } from '../spot-quote-contacts/queries'
import PanelNav from './PanelNav'
import { SentQuoteRequestsHeader } from './SentQuoteRequestsHeader'
import { SpotQuoteContactListing } from './SpotQuoteContactListing'
import { useSpotQuoteListActions, useSpotQuoteListSelected } from './store'
import { useSubmitForm } from './useSubmitForm'

type Props = {
  closePanel: () => void
  load: FullShipment
}

export const SpotQuoteRequestPanel = ({ closePanel, load }: Props) => {
  const [isViewingSentList, setViewingSentList] = useState(false)
  const groupsQuery = useSpotQuoteGroups()
  const recipientsQuery = useSpotQuoteRecipients(load.loadId)
  const emails = useSpotQuoteListSelected()
  const { setEmails, setGroupId } = useSpotQuoteListActions()

  const isLoading =
    groupsQuery.isLoading ||
    (recipientsQuery.isLoading && recipientsQuery.isFetching)

  const hasError =
    (!groupsQuery.data && groupsQuery.isError) ||
    (!recipientsQuery.data && recipientsQuery.isError)

  const { isSending, isSent, onSubmit } = useSubmitForm({
    load,
  })
  const handleSubmit = async () => {
    await onSubmit(emails)
  }
  const handleClose = () => {
    setEmails([])
    setGroupId(ALL_CONTACTS_GROUP_ID)
    closePanel()
  }

  if (isSent) {
    return (
      <DetailPanelLayout
        footer={
          <FvButton
            theme="primary"
            className="self-start"
            icon="times"
            onClick={handleClose}
          >
            Close
          </FvButton>
        }
      >
        <SentQuoteRequestsHeader emails={emails} />
      </DetailPanelLayout>
    )
  }

  const recipientEmails = recipientsQuery.data?.map(r => r.email) ?? []
  const filter = (u: SpotQuoteContact) => {
    const includes = recipientEmails.includes(u.email)
    return isViewingSentList ? includes : !includes
  }

  const footer = (
    <ValidatedForm onValidSubmit={handleSubmit} className="flex items-center">
      <FvButton
        theme="primary"
        type="submit"
        icon={isSending ? 'spinner' : 'paper-plane'}
      >
        <span>Send out for quotes</span>
      </FvButton>
      <FvButton theme="plain" onClick={handleClose} type="button" icon="times">
        Cancel
      </FvButton>
    </ValidatedForm>
  )

  return (
    <DetailPanelLayout
      header={
        <PanelNav
          closePanel={handleClose}
          isViewingSentList={isViewingSentList}
          setViewingSentList={setViewingSentList}
        />
      }
      footer={footer}
    >
      {isLoading && <InfoBox icon="spinner">Loading...</InfoBox>}
      {hasError && (
        <InfoBox icon="triangle">
          Unable to load contacts, {supportMessage}
        </InfoBox>
      )}
      <SpotQuoteContactListing
        mode={isViewingSentList ? 'recipients' : 'send'}
        filterUsers={filter}
      />
    </DetailPanelLayout>
  )
}
